export type QuoteTotalsMode = 'final_total_only' | 'detailed';

export interface QuoteTotalSettings {
  mode: QuoteTotalsMode;
  showGrandTotal: boolean;
  grandTotalLabel: string;
  showSubtotal: boolean;
  showDiscountTotal: boolean;
  taxEnabled: boolean;
  showTaxLine: boolean;
  taxLabel: string;
  taxRatePercent: number;
  taxInclusive: boolean;
}

export interface QuoteTotalsSummary {
  parts: number;
  labor: number;
  subtotal: number;
  discountTotal: number;
  taxableTotal: number;
  taxTotal: number;
  grandTotal: number;
}

export interface QuoteTotalDisplayRow {
  id: string;
  label: string;
  value: number;
  emphasised?: boolean;
  deduction?: boolean;
}

export const DEFAULT_QUOTE_TOTAL_SETTINGS: QuoteTotalSettings = {
  mode: 'final_total_only',
  showGrandTotal: true,
  grandTotalLabel: 'Total',
  showSubtotal: false,
  showDiscountTotal: false,
  taxEnabled: false,
  showTaxLine: true,
  taxLabel: 'Tax',
  taxRatePercent: 0,
  taxInclusive: false
};

const money = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Number(value.toFixed(2));
};

const nonNegativeNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return parsed;
};

const textOrDefault = (value: unknown, fallback: string): string => {
  const text = String(value || '').trim();
  return text || fallback;
};

export const normalizeQuoteTotalSettings = (value?: unknown): QuoteTotalSettings => {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const mode = raw.mode === 'detailed' ? 'detailed' : DEFAULT_QUOTE_TOTAL_SETTINGS.mode;
  return {
    mode,
    showGrandTotal:
      raw.showGrandTotal === undefined ? DEFAULT_QUOTE_TOTAL_SETTINGS.showGrandTotal : raw.showGrandTotal !== false,
    grandTotalLabel: textOrDefault(raw.grandTotalLabel, DEFAULT_QUOTE_TOTAL_SETTINGS.grandTotalLabel),
    showSubtotal:
      raw.showSubtotal === undefined ? DEFAULT_QUOTE_TOTAL_SETTINGS.showSubtotal : raw.showSubtotal === true,
    showDiscountTotal:
      raw.showDiscountTotal === undefined
        ? DEFAULT_QUOTE_TOTAL_SETTINGS.showDiscountTotal
        : raw.showDiscountTotal === true,
    taxEnabled: raw.taxEnabled === undefined ? DEFAULT_QUOTE_TOTAL_SETTINGS.taxEnabled : raw.taxEnabled === true,
    showTaxLine:
      raw.showTaxLine === undefined ? DEFAULT_QUOTE_TOTAL_SETTINGS.showTaxLine : raw.showTaxLine !== false,
    taxLabel: textOrDefault(raw.taxLabel, DEFAULT_QUOTE_TOTAL_SETTINGS.taxLabel),
    taxRatePercent: money(nonNegativeNumber(raw.taxRatePercent, DEFAULT_QUOTE_TOTAL_SETTINGS.taxRatePercent)),
    taxInclusive:
      raw.taxInclusive === undefined ? DEFAULT_QUOTE_TOTAL_SETTINGS.taxInclusive : raw.taxInclusive === true
  };
};

export const normalizeQuoteTotalsSummary = (value?: unknown): QuoteTotalsSummary => {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const parts = money(nonNegativeNumber(raw.parts));
  const labor = money(nonNegativeNumber(raw.labor));
  const taxableTotal = money(nonNegativeNumber(raw.taxableTotal, parts + labor));
  const subtotal = money(nonNegativeNumber(raw.subtotal, taxableTotal));
  const discountTotal = money(nonNegativeNumber(raw.discountTotal, Math.max(0, subtotal - taxableTotal)));
  const taxTotal = money(nonNegativeNumber(raw.taxTotal));
  const grandTotal = money(nonNegativeNumber(raw.grandTotal, taxableTotal + taxTotal));
  return {
    parts,
    labor,
    subtotal,
    discountTotal,
    taxableTotal,
    taxTotal,
    grandTotal
  };
};

export const calculateQuoteTotals = (lineItems: any[] = [], settingsInput?: unknown): QuoteTotalsSummary => {
  const settings = normalizeQuoteTotalSettings(settingsInput);
  let parts = 0;
  let labor = 0;
  let subtotal = 0;

  (Array.isArray(lineItems) ? lineItems : []).forEach(line => {
    const quantity = nonNegativeNumber(line?.quantity);
    const unitValue = nonNegativeNumber(line?.unitValue);
    const originalUnitValueRaw = nonNegativeNumber(line?.originalUnitValue, unitValue);
    const originalUnitValue = Math.max(originalUnitValueRaw, unitValue);
    const lineTotal = line?.isFree ? 0 : quantity * unitValue;
    const originalLineTotal = line?.isFree ? 0 : quantity * originalUnitValue;
    subtotal += originalLineTotal;
    if (String(line?.lineType || 'part').toLowerCase() === 'labor') {
      labor += lineTotal;
    } else {
      parts += lineTotal;
    }
  });

  const taxableTotal = money(parts + labor);
  const discountTotal = money(Math.max(0, subtotal - taxableTotal));
  const rate = settings.taxEnabled ? settings.taxRatePercent / 100 : 0;
  const taxTotal =
    rate > 0
      ? settings.taxInclusive
        ? money(taxableTotal - taxableTotal / (1 + rate))
        : money(taxableTotal * rate)
      : 0;
  const grandTotal = settings.taxEnabled && !settings.taxInclusive ? money(taxableTotal + taxTotal) : taxableTotal;

  return {
    parts: money(parts),
    labor: money(labor),
    subtotal: money(subtotal),
    discountTotal,
    taxableTotal,
    taxTotal,
    grandTotal
  };
};

const taxLabel = (settings: QuoteTotalSettings): string => {
  const label = settings.taxLabel || 'Tax';
  return settings.taxRatePercent > 0 ? `${label} (${settings.taxRatePercent}%)` : label;
};

export const buildQuoteTotalDisplayRowsFromSummary = (
  summaryInput?: unknown,
  settingsInput?: unknown
): QuoteTotalDisplayRow[] => {
  const settings = normalizeQuoteTotalSettings(settingsInput);
  const summary = normalizeQuoteTotalsSummary(summaryInput);
  const rows: QuoteTotalDisplayRow[] = [];

  if (settings.mode === 'detailed') {
    if (settings.showSubtotal) {
      rows.push({ id: 'subtotal', label: 'Subtotal', value: summary.subtotal });
    }
    if (settings.showDiscountTotal) {
      rows.push({ id: 'discount', label: 'Discount', value: summary.discountTotal, deduction: true });
    }
  }

  if (settings.taxEnabled && settings.showTaxLine) {
    rows.push({ id: 'tax', label: taxLabel(settings), value: summary.taxTotal });
  }

  if (settings.showGrandTotal) {
    rows.push({
      id: 'grandTotal',
      label: settings.grandTotalLabel || 'Total',
      value: summary.grandTotal,
      emphasised: true
    });
  }

  return rows;
};

export const buildQuoteTotalDisplayRows = (
  lineItems: any[] = [],
  settingsInput?: unknown
): QuoteTotalDisplayRow[] =>
  buildQuoteTotalDisplayRowsFromSummary(calculateQuoteTotals(lineItems, settingsInput), settingsInput);

export const formatQuoteTotalRowValue = (
  row: QuoteTotalDisplayRow,
  formatCurrency: (value: number) => string = value => `$${value.toFixed(2)}`
): string => `${row.deduction ? '-' : ''}${formatCurrency(Math.abs(row.value))}`;
